/**
 * Collection page (American Tourister style) — dynamic filters, sorting,
 * color/style variant swatches and quick add-to-cart.
 * Self-contained, no external dependencies. Drop-in for any Online Store 2.0 theme.
 */
(function () {
  'use strict';

  var ROOT_SELECTOR = '[data-cp-section]';

  /* ---------------------------------------------------------
   * Money formatting — implements Shopify's money_format spec
   * ({{amount}}, {{amount_no_decimals}}, {{amount_with_comma_separator}},
   * {{amount_no_decimals_with_comma_separator}}). Prices from Liquid/JSON
   * are always in the shop's smallest currency unit (i.e. "cents").
   * ------------------------------------------------------- */
  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    cents = Number(cents || 0);
    format = format || '${{amount}}';

    function defaultTo(value, def) { return value == null || value !== value ? def : value; }

    function formatWithDelimiters(number, precision, thousands, decimal) {
      precision = defaultTo(precision, 2);
      thousands = defaultTo(thousands, ',');
      decimal = defaultTo(decimal, '.');
      if (isNaN(number) || number == null) number = 0;
      number = (number / 100.0).toFixed(precision);

      var parts = number.split('.');
      var dollars = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1' + thousands);
      var cents2 = parts[1] ? decimal + parts[1] : '';
      return dollars + cents2;
    }

    var value = '';
    var placeholder = /\{\{\s*(\w+)\s*\}\}/;
    var match = format.match(placeholder);

    switch (match ? match[1] : '') {
      case 'amount':
        value = formatWithDelimiters(cents, 2);
        break;
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      default:
        value = formatWithDelimiters(cents, 2);
    }

    return format.replace(placeholder, value);
  }

  /* ---------------------------------------------------------
   * Helpers
   * ------------------------------------------------------- */
  function qs(sel, ctx) { return (ctx || document).querySelector(sel); }
  function qsa(sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); }

  function getShopifyRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  /* ---------------------------------------------------------
   * FACETS: filtering, sorting, pagination — via Section
   * Rendering API so the page never fully reloads.
   * ------------------------------------------------------- */
  // Factory: one independent instance per collection section on the page
  // (guards against duplicate/shared state if a theme ever renders more
  // than one instance of this section on the same page).
  function createFacets(root) {
    var instance = Object.create(FacetsProto);

    instance.root = root;
    instance.sectionId = root.getAttribute('data-section-id');
    instance.collectionUrl = root.getAttribute('data-collection-url');
    instance.form = qs('[data-facets-form]', root);
    instance.mainWrap = qs('[data-collection-main]', root);
    instance.filtersPanel = qs('[data-filters-panel]', root);
    instance.countEl = qs('[data-product-count]', root);

    instance.bindGroupToggles(root);
    instance.bindInputs(root);
    instance.bindPagination(root);
    instance.bindDrawer(root);
    instance.bindClear(root);
    instance.bindSwatchMore(root);

    window.addEventListener('popstate', instance.onPopState.bind(instance));

    return instance;
  }

  var FacetsProto = {

    bindGroupToggles: function (root) {
      qsa('[data-filter-toggle]', root).forEach(function (btn) {
        if (btn.__cpBound) return;
        btn.__cpBound = true;
        btn.addEventListener('click', function () {
          var expanded = btn.getAttribute('aria-expanded') !== 'false';
          btn.setAttribute('aria-expanded', String(!expanded));
        });
      });
    },

    bindInputs: function (root) {
      var self = this;
      qsa('[data-facet-input]', root).forEach(function (input) {
        if (input.__cpBound) return;
        input.__cpBound = true;
        if (input.type === 'checkbox') {
          input.addEventListener('change', function () { self.submit(); });
        }
      });

      qsa('[data-sort-select]', root).forEach(function (select) {
        if (select.__cpBound) return;
        select.__cpBound = true;
        select.addEventListener('change', function () {
          // keep every sort select (desktop + mobile) in sync
          qsa('[data-sort-select]', self.root).forEach(function (other) { other.value = select.value; });
          self.submit();
        });
      });

      var applyPriceBtn = qs('[data-apply-price]', root);
      if (applyPriceBtn && !applyPriceBtn.__cpBound) {
        applyPriceBtn.__cpBound = true;
        applyPriceBtn.addEventListener('click', function () { self.submit(); });
      }
    },

    bindPagination: function (root) {
      var self = this;
      var nav = qs('[data-pagination]', root);
      if (!nav) return;
      nav.addEventListener('click', function (e) {
        var link = e.target.closest('[data-page-link]');
        if (!link) return;
        e.preventDefault();
        self.fetchAndRender(link.getAttribute('href'), true);
      });
    },

    bindDrawer: function (root) {
      var overlay = qs('[data-filter-overlay]', root);
      var panel = this.filtersPanel;

      function openDrawer() {
        panel.classList.add('is-open');
        overlay.hidden = false;
        document.body.style.overflow = 'hidden';
        var openBtn = qs('[data-open-filters]', root);
        if (openBtn) openBtn.setAttribute('aria-expanded', 'true');
      }
      function closeDrawer() {
        panel.classList.remove('is-open');
        overlay.hidden = true;
        document.body.style.overflow = '';
        var openBtn = qs('[data-open-filters]', root);
        if (openBtn) openBtn.setAttribute('aria-expanded', 'false');
      }

      qsa('[data-open-filters]', root).forEach(function (btn) {
        btn.addEventListener('click', openDrawer);
      });
      qsa('[data-close-filters]', root).forEach(function (btn) {
        btn.addEventListener('click', closeDrawer);
      });
      overlay.addEventListener('click', closeDrawer);

      this._closeDrawer = closeDrawer;
    },

    bindClear: function (root) {
      var self = this;
      qsa('[data-clear-filters]', root).forEach(function (btn) {
        btn.addEventListener('click', function () {
          qsa('[data-facet-input]', root).forEach(function (input) {
            if (input.type === 'checkbox') input.checked = false;
            else input.value = '';
          });
          self.submit();
        });
      });
    },

    bindSwatchMore: function (root) {
      root.addEventListener('click', function (e) {
        var btn = e.target.closest('[data-swatch-more]');
        if (!btn) return;
        btn.closest('[data-swatches]').classList.add('is-expanded');
      });
    },

    buildParams: function () {
      var params = new URLSearchParams();

      qsa('[data-facet-input]', this.root).forEach(function (input) {
        if (input.type === 'checkbox') {
          if (input.checked) params.append(input.name, input.value);
        } else if (input.value) {
          params.append(input.name, input.value);
        }
      });

      var sortSelect = qs('[data-sort-select]', this.root);
      if (sortSelect && sortSelect.value) params.set('sort_by', sortSelect.value);

      return params;
    },

    submit: function () {
      var params = this.buildParams();
      var url = this.collectionUrl + (params.toString() ? '?' + params.toString() : '');
      this.fetchAndRender(url, false);
    },

    onPopState: function () {
      this.fetchAndRender(window.location.href, false, true);
    },

    fetchAndRender: function (url, isPagination, skipHistory) {
      var self = this;
      this.mainWrap.classList.add('is-loading');

      var sep = url.indexOf('?') === -1 ? '?' : '&';
      var fetchUrl = url + sep + 'section_id=' + this.sectionId;

      fetch(fetchUrl)
        .then(function (res) { return res.text(); })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');

          var newMain = doc.querySelector('[data-collection-main]');
          var newFilters = doc.querySelector('[data-filters-panel]');
          var newCount = doc.querySelector('[data-product-count]');

          if (newMain && self.mainWrap) self.mainWrap.innerHTML = newMain.innerHTML;
          if (newFilters && self.filtersPanel) {
            // preserve open/closed state of the drawer while swapping content
            var wasOpen = self.filtersPanel.classList.contains('is-open');
            self.filtersPanel.innerHTML = newFilters.innerHTML;
            if (wasOpen) self.filtersPanel.classList.add('is-open');
          }
          if (newCount && self.countEl) self.countEl.textContent = newCount.textContent;

          self.mainWrap.classList.remove('is-loading');

          if (!skipHistory) {
            var cleanUrl = url.replace(/([?&])section_id=[^&]+(&|$)/, '$1').replace(/[?&]$/, '');
            window.history.pushState({}, '', cleanUrl || self.collectionUrl);
          }

          // rebind everything inside the freshly-injected markup
          self.bindGroupToggles(self.root);
          self.bindInputs(self.root);
          self.bindSwatchMore(self.root);
          Cards.init(self.root);

          if (isPagination) {
            self.mainWrap.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        })
        .catch(function () {
          self.mainWrap.classList.remove('is-loading');
        });
    }
  };

  /* ---------------------------------------------------------
   * CARDS: color swatches, style dropdown, quick add-to-cart
   * ------------------------------------------------------- */
  var Cards = {

    init: function (root) {
      var moneyFormat = root.getAttribute('data-money-format') || '${{amount}}';

      qsa('.cp-card', root).forEach(function (card) {
        if (card.__cpBound) return;
        card.__cpBound = true;

        var jsonTag = qs('[data-product-json]', card);
        if (!jsonTag) return;
        var data;
        try { data = JSON.parse(jsonTag.textContent); } catch (e) { return; }

        var swatchesEl = qs('[data-swatches]', card);
        var styleSelect = qs('[data-style-select]', card);
        var addBtn = qs('[data-add-to-cart]', card);
        var img = qs('[data-card-img]', card);
        var priceCurrent = qs('[data-price-current]', card);
        var priceCompare = qs('[data-price-compare]', card);
        var badge = qs('[data-sale-badge]', card);

        function findVariant(colorValue, styleValue) {
          var exact = data.variants.find(function (v) {
            var colorMatch = !data.colorKey || v[data.colorKey] === colorValue;
            var styleMatch = !data.styleKey || v[data.styleKey] === styleValue;
            return colorMatch && styleMatch;
          });
          if (exact) return exact;

          // fall back to any variant matching the color only
          var byColor = data.variants.find(function (v) {
            return !data.colorKey || v[data.colorKey] === colorValue;
          });
          return byColor || data.variants[0];
        }

        function isColorAvailable(colorValue, styleValue) {
          return data.variants.some(function (v) {
            var colorMatch = !data.colorKey || v[data.colorKey] === colorValue;
            var styleMatch = !data.styleKey || v[data.styleKey] === styleValue;
            return colorMatch && styleMatch && v.available;
          });
        }

        function applyVariant(variant) {
          if (!variant) return;

          if (addBtn) {
            addBtn.setAttribute('data-variant-id', variant.id);
            addBtn.disabled = !variant.available;
          }
          if (img && variant.featured_image) img.src = variant.featured_image;

          if (priceCurrent) priceCurrent.textContent = formatMoney(variant.price, moneyFormat);
          var onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
          if (onSale) {
            if (!priceCompare) {
              priceCompare = document.createElement('span');
              priceCompare.className = 'cp-price-compare';
              priceCompare.setAttribute('data-price-compare', '');
              priceCurrent.insertAdjacentElement('afterend', priceCompare);
            }
            priceCompare.textContent = formatMoney(variant.compare_at_price, moneyFormat);
            priceCompare.hidden = false;
            if (badge) {
              var pct = Math.round(((variant.compare_at_price - variant.price) / variant.compare_at_price) * 100);
              badge.textContent = 'Save ' + pct + '%';
              badge.hidden = false;
            }
          } else {
            if (priceCompare) priceCompare.hidden = true;
            if (badge) badge.hidden = true;
          }
        }

        function refreshSwatches(styleValue) {
          if (!swatchesEl) return;
          qsa('[data-swatch]', swatchesEl).forEach(function (sw) {
            var colorValue = sw.getAttribute('data-color');
            var v = findVariant(colorValue, styleValue);
            var available = isColorAvailable(colorValue, styleValue);
            sw.classList.toggle('is-unavailable', !available);
            if (v) sw.setAttribute('data-variant-id', v.id);
          });
        }

        // -- color swatch click --
        if (swatchesEl) {
          swatchesEl.addEventListener('click', function (e) {
            var sw = e.target.closest('[data-swatch]');
            if (!sw || sw.hasAttribute('data-swatch-more')) return;

            qsa('[data-swatch]', swatchesEl).forEach(function (el) { el.classList.remove('is-selected'); });
            sw.classList.add('is-selected');

            var colorValue = sw.getAttribute('data-color');
            var styleValue = styleSelect ? styleSelect.value : null;
            var variant = findVariant(colorValue, styleValue);
            applyVariant(variant);
          });
        }

        // -- style dropdown change --
        if (styleSelect) {
          styleSelect.addEventListener('change', function () {
            var styleValue = styleSelect.value;
            var selectedSwatch = swatchesEl ? qs('.cp-swatch.is-selected', swatchesEl) : null;
            var colorValue = selectedSwatch ? selectedSwatch.getAttribute('data-color') : null;

            var variant = findVariant(colorValue, styleValue);
            applyVariant(variant);
            refreshSwatches(styleValue);
          });
        }

        // -- quick add to cart --
        if (addBtn) {
          addBtn.addEventListener('click', function (e) {
            e.preventDefault();
            e.stopPropagation();
            Cart.add(addBtn.getAttribute('data-variant-id'), addBtn);
          });
        }
      });
    }
  };

  /* ---------------------------------------------------------
   * CART: AJAX add-to-cart
   * ------------------------------------------------------- */
  var Cart = {
    add: function (variantId, btn) {
      if (!variantId || btn.classList.contains('is-loading')) return;

      btn.classList.add('is-loading');

      fetch(getShopifyRoot() + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: 1 })
      })
        .then(function (res) { return res.json().then(function (data) { return { ok: res.ok, data: data }; }); })
        .then(function (result) {
          btn.classList.remove('is-loading');
          if (!result.ok) {
            Cart.toast(result.data.description || 'Could not add to cart', true);
            return;
          }
          btn.classList.add('is-added');
          setTimeout(function () { btn.classList.remove('is-added'); }, 1600);

          Cart.toast('Added to cart');
          Cart.refreshCount();

          document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: result.data }));
          document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        })
        .catch(function () {
          btn.classList.remove('is-loading');
          Cart.toast('Could not add to cart', true);
        });
    },

    refreshCount: function () {
      fetch(getShopifyRoot() + 'cart.js')
        .then(function (res) { return res.json(); })
        .then(function (cart) {
          qsa('[data-cart-count]').forEach(function (el) { el.textContent = cart.item_count; });
        })
        .catch(function () {});
    },

    toast: function (message, isError) {
      var toast = qs('[data-add-to-cart-toast]');
      if (!toast) return;
      toast.textContent = message;
      toast.classList.toggle('cp-toast--error', !!isError);
      toast.hidden = false;
      requestAnimationFrame(function () { toast.classList.add('is-visible'); });
      clearTimeout(toast.__cpTimer);
      toast.__cpTimer = setTimeout(function () {
        toast.classList.remove('is-visible');
        setTimeout(function () { toast.hidden = true; }, 200);
      }, 2200);
    }
  };

  /* ---------------------------------------------------------
   * Boot
   * ------------------------------------------------------- */
  function boot() {
    qsa(ROOT_SELECTOR).forEach(function (root) {
      root.__cpFacets = createFacets(root);
      Cards.init(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
